import axios from "axios";
import { clearCachedScope } from "./lib/dataCache";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "arctic_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Authorization failures invalidate the current identity's read cache before
// page-level error handling runs. This prevents a 401/403 refresh failure from
// rendering previously cached customer, seller, or admin data as a fallback.
// The authentication provider decides whether the token itself must be removed.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    try {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = (detail && typeof detail === "object") ? detail.code : null;

      if (status === 401 || status === 403) {
        clearCachedScope();
      }

      const onChangePage =
        typeof window !== "undefined" &&
        window.location &&
        window.location.pathname === "/change-password";
      if (status === 403 && code === "PASSWORD_RESET_REQUIRED" && !onChangePage) {
        window.location.assign("/change-password");
      }
    } catch (interceptorErr) {
      /* never let the interceptor itself throw — surface in dev only */
      if (process.env.NODE_ENV !== "production") {
        console.warn("[api interceptor] swallowed error:", interceptorErr);
      }
    }
    return Promise.reject(err);
  },
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join("; ");
  // Structured backend errors like { code: "...", message: "..." } — surface
  // the human-readable `message` first, then fall back to stringification.
  if (typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message.length > 0) {
      return detail.message;
    }
    try {
      return JSON.stringify(detail);
    } catch {
      return "Something went wrong";
    }
  }
  return String(detail);
}

// Customer-facing error helper. Never exposes status codes, axios messages,
// stack traces, or backend diagnostics. Backend may still send a `detail`
// string with a user-facing reason (e.g. "Vendor not available") — those
// are short and friendly enough to pass through. Anything that looks like
// an axios/network failure ("Network Error", "Request failed with status…",
// "timeout of …", "ECONNREFUSED") is replaced with a warm fallback.
export function friendlyApiMessage(err, fallback) {
  const raw = formatApiError(err);
  const lower = String(raw || "").toLowerCase();
  // If the error has no backend response payload, it is by definition a
  // client-side / network failure (fetch aborted, DNS, CORS, thrown
  // Error object with an arbitrary message).  Never surface that raw
  // string to the customer — always use the friendly fallback.
  const hasBackendDetail = err?.response?.data?.detail != null;
  const looksTechnical =
    /request failed|status code|network error|network$|^network|timeout|econnrefused|enotfound|axios|fetch failed|cors|stack/.test(lower);
  if (!hasBackendDetail || looksTechnical || !raw) {
    return fallback || "Something went wrong. Please try again shortly.";
  }
  // Backend-sent friendly detail (e.g. "Vendor not available") — pass through.
  return raw;
}

export const STATUS_LABELS = {
  created: "Awaiting payment",
  pending_vendor_acceptance: "Awaiting vendor",
  accepted: "Accepted",
  preparing: "Preparing",
  ready_for_pickup: "Ready for pickup",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const STATUS_ORDER = [
  "created",
  "pending_vendor_acceptance",
  "accepted",
  "preparing",
  "ready_for_pickup",
  "completed",
];
